import { files as filesApi } from '@afosto/content/api/Files';
import useIntl from '@afosto/utils-react/intl/useIntl';
import FilePondPluginValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { useConfirm } from '../ConfirmDialog';
import translations from './translations';
import './styles.css';

registerPlugin(FilePondPluginValidateSize);
registerPlugin(FilePondPluginFileValidateType);

const FileUpload = ({
  required,
  disabled,
  captureMethod,
  allowDrop,
  allowBrowse,
  allowPaste,
  allowMultiple,
  allowReplace,
  allowRevert,
  forceRevert,
  checkValidity,
  itemInsertLocation,
  itemInsertInterval,
  dropOnPage,
  dropOnElement,
  dropValidation,
  ignoredFiles,
  instantUpload,
  files,
  onRevertFile,
  onRemoveFile,
  onUploadFile,
  labelDecimalSeparator,
  labelThousandsSeparator,
  allowFileSizeValidation,
  maxFileSize,
  maxTotalFileSize,
  allowFileTypeValidation,
  acceptedFileTypes,
  labelFileTypeNotAllowed,
  fileValidateTypeLabelExpectedTypes,
  fileValidateTypeLabelExpectedTypesMap,
  fileValidateTypeDetectType,
  fileUploadRef,
  uploadMethod,
  uploadPath,
  listUploadedFiles,
  onProcessFiles,
  onAddFile,
  onError,
  isPublic,
  ...otherProps
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [lastSignature, setLastSignature] = useState(null);
  const confirm = useConfirm();
  const intl = useIntl();

  useEffect(() => {
    setUploadedFiles(files);
  }, [setUploadedFiles, files]);

  const handleUpdateFiles = fileItems => {
    setUploadedFiles(fileItems.map(fileItem => fileItem.file));
  };

  const handleConfirmDuplicateFile = async file =>
    confirm({
      title: intl.formatMessage(translations.duplicateFileDialog.title),
      description: intl.formatMessage(translations.duplicateFileDialog.description, {
        filename: file.name,
      }),
      catchOnCancel: false,
      buttons: [
        {
          label: intl.formatMessage(translations.duplicateFileDialog.buttons.cancel),
          type: 'cancel',
          variant: 'outlined',
          style: {
            marginRight: 'auto',
          },
        },
        {
          label: intl.formatMessage(translations.duplicateFileDialog.buttons.resolve),
          response: 'resolve',
          type: 'submit',
          variant: 'contained',
        },
        {
          label: intl.formatMessage(translations.duplicateFileDialog.buttons.upsert),
          response: 'upsert',
          type: 'submit',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

  const defaultServerConfig = {
    fetch: null,
    load: null,
    revert: (uniqueFileId, load) => {
      if (onRevertFile) {
        onRevertFile(uniqueFileId);
      }

      load();
    },
    remove: (source, load) => {
      if (onRemoveFile) {
        onRemoveFile(source);
      }

      load();
    },
    process: (fieldName, file, metadata, load, error, progress, abort) => {
      const uploadFile = async method => {
        try {
          let currentSignature = uploadMethod !== 'strict' ? lastSignature : null;
          let isExpired = false;

          if (currentSignature) {
            const signatureExpiresAt = new Date(currentSignature.expires_at).getTime();
            const currentTime = new Date().getTime();

            isExpired = signatureExpiresAt - currentTime <= 0;
          }

          const formData = new FormData();
          formData.append('file', file, file.name);

          if (!currentSignature || isExpired) {
            const signedUrlResponse = await filesApi.createSignedUrl({
              is_public: isPublic,
              path: uploadPath,
              method: method ?? uploadMethod,
              is_listed: listUploadedFiles,
            });
            const { signature } = signedUrlResponse.data;

            currentSignature = signature;
            setLastSignature(signature);
          }

          const response = await filesApi.uploadFile(currentSignature, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: progressEvent => {
              progress(progressEvent.lengthComputable, progressEvent.loaded, progressEvent.total);
            },
          });

          const [fileResponse] = response.data;
          load(fileResponse.id);

          if (onUploadFile) {
            onUploadFile(fileResponse);
          }

          return true;
        } catch (err) {
          return Promise.reject(err);
        }
      };

      const handleUploadFile = async () => {
        try {
          await uploadFile();
        } catch (err) {
          if (uploadMethod === 'strict' && err?.response?.status === 409) {
            const confirmResponse = await handleConfirmDuplicateFile(file).catch(() => null);

            if (confirmResponse === 'upsert') {
              await uploadFile('upsert').catch(uploadError => error(uploadError?.message));
            }

            if (confirmResponse === 'resolve') {
              await uploadFile('resolve').catch(uploadError => error(uploadError?.message));
            }

            if (confirmResponse === 'cancel') {
              error(err.message);
            }

            if (!confirmResponse) {
              error(err.message);
            }
          } else {
            error(err.message);
          }
        }
      };

      handleUploadFile();

      return {
        abort: () => {
          abort();
        },
      };
    },
  };

  const labelProperties = [
    'labelIdle',
    'labelInvalidField',
    'labelFileWaitingForSize',
    'labelFileSizeNotAvailable',
    'labelFileLoading',
    'labelFileLoadError',
    'labelFileProcessing',
    'labelFileProcessingComplete',
    'labelFileProcessingAborted',
    'labelFileProcessingError',
    'labelFileProcessingRevertError',
    'labelFileRemoveError',
    'labelTapToCancel',
    'labelTapToRetry',
    'labelTapToUndo',
    'labelButtonRemoveItem',
    'labelButtonAbortItemLoad',
    'labelButtonRetryItemLoad',
    'labelButtonAbortItemProcessing',
    'labelButtonUndoItemProcessing',
    'labelButtonRetryItemProcessing',
    'labelButtonProcessItem',
    'labelMaxFileSizeExceeded',
    'labelMaxTotalFileSizeExceeded',
    'labelFileTypeNotAllowed',
  ].reduce(
    (acc, labelKey) => ({
      ...acc,
      [labelKey]: otherProps[labelKey] || intl.formatMessage(translations[labelKey]),
    }),
    {},
  );

  const fileSizeLabelProperties = ['labelMaxFileSize', 'labelMaxTotalFileSize'].reduce(
    (acc, labelKey) => ({
      ...acc,
      [labelKey]:
        otherProps[labelKey] ||
        intl.formatMessage(translations[labelKey], {
          filesize: labelKey === 'labelMaxFileSize' ? maxFileSize : maxTotalFileSize,
        }),
    }),
    {},
  );

  const fileTypeLabelProperties = ['fileValidateTypeLabelExpectedTypes'].reduce(
    (acc, labelKey) => ({
      ...acc,
      [labelKey]:
        otherProps[labelKey] ||
        intl.formatMessage(translations[labelKey], {
          allTypes: acceptedFileTypes,
          allButLastType: acceptedFileTypes?.slice(0, -1),
          lastType: acceptedFileTypes?.slice(-1)?.[0],
        }),
    }),
    {},
  );

  const isStrict = uploadMethod === 'strict';
  const strictProps = isStrict
    ? {
        maxParallelUploads: 1,
      }
    : {};

  return (
    <FilePond
      allowMultiple={allowMultiple}
      ref={fileUploadRef}
      server={defaultServerConfig}
      required={required}
      disabled={disabled}
      captureMethod={captureMethod}
      allowDrop={allowDrop}
      allowBrowse={allowBrowse}
      allowPaste={allowPaste}
      allowReplace={allowReplace}
      allowRevert={allowRevert}
      forceRevert={forceRevert}
      checkValidity={checkValidity}
      itemInsertLocation={itemInsertLocation}
      itemInsertInterval={itemInsertInterval}
      dropOnPage={dropOnPage}
      dropOnElement={dropOnElement}
      dropValidation={dropValidation}
      ignoredFiles={ignoredFiles}
      instantUpload={instantUpload}
      files={uploadedFiles}
      onupdatefiles={handleUpdateFiles}
      labelDecimalSeparator={labelDecimalSeparator}
      labelThousandsSeparator={labelThousandsSeparator}
      onprocessfiles={onProcessFiles}
      onaddfile={onAddFile}
      onerror={onError}
      allowFileSizeValidation={allowFileSizeValidation}
      maxFileSize={maxFileSize}
      maxTotalFileSize={maxTotalFileSize}
      allowFileTypeValidation={allowFileTypeValidation}
      acceptedFileTypes={acceptedFileTypes}
      labelFileTypeNotAllowe={labelFileTypeNotAllowed}
      fileValidateTypeLabelExpectedTypes={fileValidateTypeLabelExpectedTypes}
      fileValidateTypeLabelExpectedTypesMap={fileValidateTypeLabelExpectedTypesMap}
      fileValidateTypeDetectType={fileValidateTypeDetectType}
      {...strictProps}
      {...labelProperties}
      {...fileSizeLabelProperties}
      {...fileTypeLabelProperties}
    />
  );
};

FileUpload.propTypes = {
  allowMultiple: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  captureMethod: PropTypes.string,
  allowDrop: PropTypes.bool,
  allowBrowse: PropTypes.bool,
  allowPaste: PropTypes.bool,
  allowReplace: PropTypes.bool,
  allowRevert: PropTypes.bool,
  fileValidateTypeDetectType: PropTypes.func,
  fileValidateTypeLabelExpectedTypes: PropTypes.string,
  fileValidateTypeLabelExpectedTypesMap: PropTypes.object,
  forceRevert: PropTypes.bool,
  checkValidity: PropTypes.bool,
  itemInsertLocation: PropTypes.string,
  itemInsertInterval: PropTypes.number,
  dropOnPage: PropTypes.bool,
  dropOnElement: PropTypes.bool,
  dropValidation: PropTypes.bool,
  ignoredFiles: PropTypes.array,
  instantUpload: PropTypes.bool,
  files: PropTypes.array,
  onRemoveFile: PropTypes.func,
  onRevertFile: PropTypes.func,
  onUploadFile: PropTypes.func,
  labelDecimalSeparator: PropTypes.string,
  labelThousandsSeparator: PropTypes.string,
  labelIdle: PropTypes.string,
  labelInvalidField: PropTypes.string,
  labelFileWaitingForSize: PropTypes.string,
  labelFileSizeNotAvailable: PropTypes.string,
  labelFileTypeNotAllowed: PropTypes.string,
  labelFileLoading: PropTypes.string,
  labelFileLoadError: PropTypes.string,
  labelFileProcessing: PropTypes.string,
  labelFileProcessingComplete: PropTypes.string,
  labelFileProcessingAborted: PropTypes.string,
  labelFileProcessingError: PropTypes.string,
  labelFileProcessingRevertError: PropTypes.string,
  labelFileRemoveError: PropTypes.string,
  labelTapToCancel: PropTypes.string,
  labelTapToRetry: PropTypes.string,
  labelTapToUndo: PropTypes.string,
  labelButtonRemoveItem: PropTypes.string,
  labelButtonAbortItemLoad: PropTypes.string,
  labelButtonRetryItemLoad: PropTypes.string,
  labelButtonAbortItemProcessing: PropTypes.string,
  labelButtonUndoItemProcessing: PropTypes.string,
  labelButtonRetryItemProcessing: PropTypes.string,
  labelButtonProcessItem: PropTypes.string,
  fileUploadRef: PropTypes.object,
  uploadMethod: PropTypes.oneOf(['resolve', 'strict', 'upsert']),
  uploadPath: PropTypes.string,
  listUploadedFiles: PropTypes.bool,
  onProcessFiles: PropTypes.func,
  onAddFile: PropTypes.func,
  onError: PropTypes.func,
  allowFileSizeValidation: PropTypes.bool,
  maxFileSize: PropTypes.string,
  maxTotalFileSize: PropTypes.string,
  isPublic: PropTypes.bool,
  allowFileTypeValidation: PropTypes.bool,
  acceptedFileTypes: PropTypes.array,
};

FileUpload.defaultProps = {
  allowMultiple: false,
  allowFileTypeValidation: false,
  acceptedFileTypes: [],
  required: false,
  disabled: false,
  captureMethod: null,
  allowDrop: true,
  allowBrowse: true,
  allowPaste: true,
  allowReplace: true,
  allowRevert: true,
  forceRevert: false,
  checkValidity: false,
  itemInsertLocation: 'before',
  itemInsertInterval: 75,
  dropOnPage: false,
  dropOnElement: true,
  dropValidation: false,
  ignoredFiles: ['.ds_store', 'thumbs.db', 'desktop.ini'],
  instantUpload: true,
  files: [],
  fileValidateTypeDetectType: null,
  fileValidateTypeLabelExpectedTypes: null,
  fileValidateTypeLabelExpectedTypesMap: {},
  onRemoveFile: undefined,
  onRevertFile: undefined,
  onUploadFile: undefined,
  labelDecimalSeparator: 'auto',
  labelThousandsSeparator: 'auto',
  labelIdle: null,
  labelInvalidField: null,
  labelFileWaitingForSize: null,
  labelFileSizeNotAvailable: null,
  labelFileTypeNotAllowed: null,
  labelFileLoading: null,
  labelFileLoadError: null,
  labelFileProcessing: null,
  labelFileProcessingComplete: null,
  labelFileProcessingAborted: null,
  labelFileProcessingError: null,
  labelFileProcessingRevertError: null,
  labelFileRemoveError: null,
  labelTapToCancel: null,
  labelTapToRetry: null,
  labelTapToUndo: null,
  labelButtonRemoveItem: null,
  labelButtonAbortItemLoad: null,
  labelButtonRetryItemLoad: null,
  labelButtonAbortItemProcessing: null,
  labelButtonUndoItemProcessing: null,
  labelButtonRetryItemProcessing: null,
  labelButtonProcessItem: null,
  fileUploadRef: undefined,
  uploadMethod: 'strict',
  uploadPath: '/',
  listUploadedFiles: true,
  onProcessFiles: undefined,
  onAddFile: undefined,
  onError: undefined,
  allowFileSizeValidation: false,
  maxFileSize: null,
  maxTotalFileSize: null,
  isPublic: false,
};

export default FileUpload;
